import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import * as api from "../services/projectApi";
import { useAuth } from "../../auth/AuthContext";
import StrategyViewer from "../components/StrategyViewer";
import AdAccountPanel from "../../ad-account/AdAccountPanel";
import VersionHistory from "../components/VersionHistory";
import NotesPanel from "../components/NotesPanel";
import FilesPanel from "../components/FilesPanel";
import ActivityFeed from "../components/ActivityFeed";
import PerformanceView from "../performance/PerformanceView";
import SyncStatusPanel from "../performance/SyncStatusPanel";
import ChatPanel from "../performance/ChatPanel";

const WORKSPACE_TABS = [
  { key: "overview", label: "Strategy" },
  { key: "performance", label: "Performance" },
  { key: "chat", label: "AI Chat" },
  { key: "settings", label: "Settings" },
];

const STEP_LABELS = {
  details: "Project details",
  objective: "Objective",
  platforms: "Platforms",
  kpis: "KPIs",
  budget: "Budget",
  timeline: "Timeline",
  context: "Business context",
  strategy: "AI strategy",
};

// Plain-language labels for stored business goals (keyed by goalKey).
const GOAL_HEADLINE = {
  orders: "Get more orders",
  leads: "Get more leads",
  reach: "Reach more people",
  visits: "Get more website visitors",
};

const GEN_STEPS = [
  "Analyzing business",
  "Building audience",
  "Calculating budget",
  "Finding opportunities",
  "Generating marketing strategy",
];

function GeneratingOverlay() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => Math.min(s + 1, GEN_STEPS.length - 1)), 16000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="proj-gen-overlay">
      <div className="proj-gen-card">
        <div className="proj-spinner" />
        <h3>Generating your marketing strategy</h3>
        <ul className="proj-gen-steps">
          {GEN_STEPS.map((label, i) => (
            <li key={i} className={i < step ? "done" : i === step ? "active" : ""}>
              {i < step ? "✓" : i === step ? "…" : "•"} {label}
            </li>
          ))}
        </ul>
        <p className="proj-muted">This usually takes 30–90 seconds. Please keep this tab open.</p>
      </div>
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { has, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "overview";
  const setTab = (key) => setSearchParams(key === "overview" ? {} : { tab: key });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [actionErr, setActionErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getDashboard(id));
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Surface the result of an ad-account OAuth redirect (?connected / ?ad_error).
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("ad_error")) setActionErr(`Ad account connection failed: ${p.get("ad_error")}`);
    if (p.get("connected") || p.get("ad_error")) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const generate = async () => {
    setActionErr(null);
    setGenerating(true);
    try {
      await api.generateStrategy(id);
      await load();
    } catch (e) {
      setActionErr(e.response?.data?.message || e.message);
    } finally {
      setGenerating(false);
    }
  };

  const decide = async (approve) => {
    setActionErr(null);
    try {
      if (approve) {
        await api.approveStrategy(id);
      } else {
        const reason = window.prompt("Reason for rejecting this strategy?");
        if (!reason) return;
        await api.rejectStrategy(id, reason);
      }
      await load();
    } catch (e) {
      setActionErr(e.response?.data?.message || e.message);
    }
  };

  if (loading) return <div className="proj-center proj-muted">Loading…</div>;
  if (error) return <div className="proj-page"><div className="proj-alert">{error}</div></div>;
  if (!data) return null;

  const { overview, objectives, goals, platforms, budget, timeline, kpis, businessContext, strategy, progress } = data;
  const notReady = progress.steps && Object.entries(progress.steps).filter(([k, v]) => !v && k !== "strategy");
  const missing = (notReady || []).map(([k]) => STEP_LABELS[k]);
  const isOwner = overview.owner?.id === user?.id;
  // The owner, or an admin/manager acting on their behalf, may link an account.
  const canLink = isOwner || has("strategy.generate") || has("project.update");
  const canGenerate = missing.length === 0 && data.adAccountLinked;

  return (
    <div className="proj-page">
      {generating && <GeneratingOverlay />}

      <div className="page-header">
        <div>
          <button className="proj-link" onClick={() => navigate("/projects")}>
            ← Projects
          </button>
          <h1 className="page-title">{overview.projectName}</h1>
          <p className="proj-muted">
            {overview.businessName}
            {overview.industry ? ` · ${overview.industry}` : ""} ·{" "}
            <span className="proj-badge-inline">{overview.status}</span>
            {overview.owner ? ` · Owner: ${overview.owner.name}` : ""}
          </p>
        </div>
        {has("project.update") && (
          <button className="proj-btn" onClick={() => navigate(`/projects/${id}/wizard`)}>
            Edit setup
          </button>
        )}
      </div>

      {/* Workspace tabs */}
      <div className="proj-tabs" role="tablist">
        {WORKSPACE_TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`proj-tab ${tab === t.key ? "active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "performance" && (
        <div className="proj-body">
          <PerformanceView projectId={id} currency={overview.currency} canSync={canLink} />
        </div>
      )}

      {tab === "chat" && (
        <div className="proj-body">
          <div className="proj-panel">
            <ChatPanel projectId={id} />
          </div>
        </div>
      )}

      {tab === "settings" && (
        <div className="proj-body">
          <AdAccountPanel projectId={id} canLink={canLink} onChange={load} />
          <SyncStatusPanel projectId={id} canSync={canLink} />
          <div className="proj-panel">
            <h4>Reporting</h4>
            <p><strong>Currency:</strong> {overview.currency || "—"}</p>
            <p><strong>Timezone:</strong> {overview.timezone || "UTC (default)"}</p>
          </div>
        </div>
      )}

      <div className="proj-body" style={tab === "overview" ? undefined : { display: "none" }}>
        {/* Progress */}
        <div className="proj-panel">
          <div className="proj-progress-head">
            <strong>Setup progress</strong>
            <span className="proj-muted">
              {progress.done}/{progress.total} · {progress.percent}%
            </span>
          </div>
          <div className="proj-progress-bar">
            <div className="proj-progress-fill" style={{ width: `${progress.percent}%` }} />
          </div>
          <div className="proj-steps">
            {Object.entries(progress.steps).map(([k, done]) => (
              <span key={k} className={`proj-step ${done ? "done" : ""}`}>
                {done ? "✓" : "○"} {STEP_LABELS[k]}
              </span>
            ))}
          </div>
        </div>

        {/* Wizard data summary */}
        <div className="proj-grid-2">
          <div className="proj-panel">
            <h4>Objective &amp; Platforms</h4>
            <p><strong>Objectives:</strong> {objectives?.length ? objectives.map((o) => o.objective).join(", ") : "—"}</p>
            <p>
              <strong>Platforms:</strong>{" "}
              {platforms?.length ? platforms.map((p) => p.platform).join(", ") : "—"}
            </p>
          </div>
          <div className="proj-panel">
            <h4>Budget</h4>
            {budget ? (
              <p>
                {budget.currency} {budget.totalBudget} total · {budget.dailyBudget || "—"}/day ·
                Google {budget.googlePercentage}% / Meta {budget.metaPercentage}%
              </p>
            ) : (
              <p className="proj-muted">Not set</p>
            )}
          </div>
          <div className="proj-panel">
            <h4>Timeline</h4>
            {timeline ? (
              <p>
                {String(timeline.startDate).slice(0, 10)} → {String(timeline.endDate).slice(0, 10)} ·{" "}
                {timeline.duration} days
              </p>
            ) : (
              <p className="proj-muted">Not set</p>
            )}
          </div>
          <div className="proj-panel">
            <h4>Goals &amp; KPIs</h4>
            {goals?.length > 0 && (
              <ul className="strat-list">
                {goals.map((g) => (
                  <li key={g.id}>
                    <strong>{GOAL_HEADLINE[g.goalKey] || g.goalKey}:</strong>{" "}
                    {g.targetMode === "ai_recommended" || g.targetValue == null
                      ? <span className="proj-muted">AdIntel to recommend</span>
                      : `${g.targetValue}/${g.period}`}
                  </li>
                ))}
              </ul>
            )}
            {/* Targeted KPIs (with a number); auto-tracked metrics are shown as chips. */}
            {kpis?.length ? (
              <>
                <ul className="strat-list">
                  {kpis.filter((k) => k.target != null).map((k) => (
                    <li key={k.id}>
                      {k.metric} {k.operator} {k.target} {k.unit} <span className="proj-muted">({k.priority})</span>
                    </li>
                  ))}
                </ul>
                {kpis.some((k) => k.target == null) && (
                  <p className="proj-sub" style={{ marginTop: 8 }}>
                    Also tracked: {kpis.filter((k) => k.target == null).map((k) => k.metric).join(", ")}
                  </p>
                )}
              </>
            ) : (
              !goals?.length && <p className="proj-muted">None</p>
            )}
          </div>
        </div>

        {businessContext && (
          <div className="proj-panel">
            <h4>Business Context</h4>
            <p><strong>Product:</strong> {businessContext.product || "—"}</p>
            <p><strong>USP:</strong> {businessContext.usp || "—"}</p>
            <p><strong>Audience:</strong> {businessContext.idealCustomer || "—"}</p>
            <p><strong>Competitors:</strong> {businessContext.competitors || "—"}</p>
          </div>
        )}

        {/* Ad accounts — always visible so connected Google/Meta accounts show. */}
        {(canLink || data.adAccounts?.length > 0) && (
          <AdAccountPanel projectId={id} canLink={canLink} onChange={load} />
        )}

        {/* Strategy */}
        <div className="proj-panel proj-strategy-panel">
          <div className="proj-progress-head">
            <strong>AI Marketing Strategy</strong>
            {strategy && (
              <span className="proj-badge-inline">
                {strategy.status} · v{strategy.currentVersion}
              </span>
            )}
          </div>

          {actionErr && <div className="proj-alert">{actionErr}</div>}

          {!strategy?.latest && (
            <div className="proj-strategy-empty">
              {missing.length > 0 && (
                <div className="proj-alert-soft">
                  Complete these before generating a strategy: {missing.join(", ")}.
                </div>
              )}

              {!data.adAccountLinked && (
                <div className="proj-alert-soft">
                  {canLink
                    ? "Link a Google or Meta ad account (in the Ad accounts section above) to generate a strategy."
                    : "An ad account must be linked to this project before a strategy can be generated."}
                </div>
              )}

              {canGenerate && has("strategy.generate") && (
                <>
                  <p className="proj-muted">All set. Generate an AI marketing strategy from your plan.</p>
                  <button className="proj-btn proj-btn-primary" onClick={generate} disabled={generating}>
                    Generate Strategy
                  </button>
                </>
              )}
            </div>
          )}

          {strategy?.latest && (
            <>
              <div className="proj-strategy-actions">
                {has("strategy.generate") && (
                  <button className="proj-btn" onClick={generate} disabled={generating}>
                    Regenerate
                  </button>
                )}
                {has("strategy.approve") && strategy.status !== "approved" && (
                  <>
                    <button className="proj-btn proj-btn-green" onClick={() => decide(true)}>
                      Approve
                    </button>
                    <button className="proj-btn proj-btn-danger" onClick={() => decide(false)}>
                      Reject
                    </button>
                  </>
                )}
              </div>
              <StrategyViewer strategy={strategy.latest.strategy} />
              <VersionHistory
                projectId={id}
                currentVersion={strategy.currentVersion}
                onRestored={load}
              />
            </>
          )}
        </div>

        {/* Notes + Files */}
        <div className="proj-grid-2">
          <NotesPanel projectId={id} />
          <FilesPanel projectId={id} />
        </div>

        {/* Activity */}
        <div className="proj-panel">
          <h4>Recent activity</h4>
          <ActivityFeed items={data.activity} />
        </div>
      </div>
    </div>
  );
}
